import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "@/components/button/Button";
import { Card } from "@/components/card/Card";
import { XIcon as X } from "@phosphor-icons/react";
import iconArrow from "@/assets/icon-arrow.svg";

export default function LoginScreen({
  onContinue,
  setAuthToken,
}: {
  onContinue: () => void;
  setAuthToken: (token: string) => void;
}) {
  const {
    user,
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth0();
  const queryParameters = new URLSearchParams(window.location.search);
  const authError = queryParameters.get("error");

  async function checkUser() {
    if (isAuthenticated && user?.email) {
      try {
        const token = await getAccessTokenSilently();
        setAuthToken(token || "");
        onContinue();
      } catch (err) {
        // handle error (optional)
        console.error("Error fetching user data:", err);
      }
      window.parent.postMessage("sc-iframe-loaded", "*");
      return;
    }
    // If not authenticated, try silent login
    // If silent login fails, redirect to login page
    // Note: Silent login requires a user session, so it will not work if the user
    else if (
      isLoading ||
      authError === "login_required" ||
      authError === "consent_required"
    ) {
      if (authError === "login_required" || authError === "consent_required") {
        window.parent.postMessage("sc-iframe-loaded", "*");
      }
      return;
    } else {
      loginWithRedirect({
        authorizationParams: {
          prompt: "none",
        },
      });
    }
  }

  useEffect(() => {
    checkUser();
  }, [isLoading]);

  const closeWindow = () => {
    window.parent.postMessage("sc-closeIframe", "*");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="h-[100vh] w-full flex justify-center items-center bg-fixed overflow-hidden">
        {/* Main Chat Container */}
        <div className="h-[100vh] w-full mx-auto max-w-lg flex flex-col rounded-md overflow-hidden relative border border-neutral-300 bg-white">
          <div className="chat-bot-header px-4 py-3 border-b border-neutral-300 flex items-center gap-3 sticky top-0 z-10">
            <div className="flex-1">
              <h2 className="mt-1 font-bold text-lg text-headline text-white">
                Kois Center Collective Intelligence
              </h2>
            </div>
            <Button
              variant="ghost"
              size="md"
              shape="square"
              className="rounded-full h-9 w-9"
              onClick={closeWindow}
            >
              <X size={20} />
            </Button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="h-full flex items-center justify-center">
              <Card className="w-full rounded-lg p-10 btn-secondary max-w-md mx-auto intro-bg">
                <div className="text-center space-y-2">
                  <h2 className="text-headline text-[32px]">Chat with John</h2>
                  <p className="text-base">
                    The Kois Center Collective Intelligence AI Chatbot
                  </p>
                  <Button
                    className="btn--login"
                    onClick={() => {
                      window.parent.postMessage("sc-chatbotLogin", "*");
                    }}
                  >
                    Login
                  </Button>
                  <a href="#" className="text-btn-arrow" onClick={onContinue}>
                    Continue without login{" "}
                    <img src={iconArrow} alt="Arrow Icon" />
                  </a>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // If authenticated, show nothing (will redirect to App)
  return null;
}
